<template>
  <!-- // TODO: [Sportybet] -->
  <BottomWrapper
    id="rule-engine-bottom-wrapper"
    :buttons="buttons"
    :is-fixed="!isNoRecord"
    :err-info="errInfo"
    cancelable-request
    @select="proceed"
    @retry="cleanErr"
    @support-request:cancel="$emit('support-request:cancel', $event)"
  >
    <div class="transactions" :class="{ 'ticket-creation-triggered': isTicketCreationTriggered }">
      <div class="bottom__question" :class="{ 'no-record': isNoRecord }">
        {{ title }}
      </div>
      <div
        v-for="transaction in uiTransactions"
        :key="transaction.shortId"
        class="m-b-10 transaction"
        :class="{
          expanded: expandedTrans.includes(transaction.shortId),
          selected: isSelectedTransaction(transaction),
        }"
      >
        <BetTransactionItem
          :transaction="transaction"
          :is-selected="isSelectedTransaction(transaction)"
          :selected-trans-issue="selectedTransIssue"
          :select-issue-transactions="selectIssueTransactions"
          :is-single-transaction-select-mode="isSingleTransactionSelectMode"
          :transaction-type="transactionType"
          :expand-trans="expandTrans"
          :expanded-trans="expandedTrans"
        />
        <template v-if="showUserEventsSelections && firstSelectedTransaction.shortId === transaction.shortId">
          <div
            v-if="VALIDATE_SELECTION_FOR_USER_EVENTS && !userEventsSelections.length"
            class="ml-1 mt-1"
            :style="{ color: 'red' }"
          >
            {{ tConversation('page.issue.bet.select_at_least_one_selection') }}
          </div>
          <div
            v-for="selection in transaction.selections"
            :key="selection.id"
            class="m-b-10 ml-1 mt-1 transaction"
            :class="{
              expandedUserSelections: expandedUserEventsSelections.includes(selection.id),
              selected: isUserEventSelectionSelected(selection),
            }"
          >
            <BetTransactionItem
              :transaction="updatedTransaction(transaction, selection)"
              :is-selected="isUserEventSelectionSelected(selection)"
              :is-single-transaction-select-mode="isSingleTransactionSelectMode"
              :transaction-type="transactionType"
              is-user-events-selection
              :user-events-selections="userEventsSelections"
              :handle-select-user-events-selections="handleSelectUserEventsSelections"
              :expanded-user-events-selections="expandedUserEventsSelections"
              :handle-expand-user-selections="handleExpandUserSelections"
            />
          </div>
        </template>
      </div>
      <div v-if="currentPage === totalPages" class="m-b-10 transaction" :class="{ selected: noneSelected }">
        <div class="transaction__header">
          <span v-if="noneSelected" @click.prevent="selectNone">
            <FaCheckSquare
              v-if="!isSingleTransactionSelectMode"
              height="2.1rem"
              width="2.1rem"
              class="checked transaction__checkbox"
            />
            <RadioFilled v-else height="2.1rem" width="2.1rem" class="checked transaction__checkbox" />
          </span>
          <span v-else @click.prevent="selectNone">
            <FaRegSquare
              v-if="!isSingleTransactionSelectMode"
              height="2.1rem"
              width="2.1rem"
              class="transaction__checkbox unchecked"
            />
            <RadioEmpty v-else height="2.1rem" width="2.1rem" class="transaction__checkbox unchecked" />
          </span>
          <div class="separate" />
          <div class="title">
            <div class="title__description">
              {{ tConversation('page.issue.none_of_these') }}
            </div>
          </div>
        </div>
      </div>
      <ListControls
        :current-page.sync="currentPage"
        :transaction-status.sync="filterTransactionStatus"
        :total-pages="totalPages"
        :placeholder="tConversation('page.issue.bet_status')"
        :transaction-statuses="availableTransactionStatuses"
      />
    </div>
    <div v-if="isTicketCreationTriggered" class="trigger-ticket-creation-warning">
      {{ ticketCreationResponseMessage }}
    </div>
    <div v-if="triggerTicketCreationError" class="trigger-ticket-creation-warning">
      {{ triggerTicketCreationError }}
    </div>
  </BottomWrapper>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Input from '@/components/Input.vue';
import { namespace } from 'vuex-class';
import { BottomWrapper } from '../../BottomWrapper.vue';
import { BetHistoryEntity, BetHistoryEntitySelection } from '@/common/api/model';
import { OrderType, SYSTEM_MESSAGE_PREFIX, TransactionType } from '@/common/config';
import * as HardcodedTags from '@/common/tags';
import { notEmpty } from '@/common/utils';
import { PleasedUserInfo, SelectedEntity, TransactionInputEvent } from '@/common/types';
import FaCheckSquare from '@/components/icons/FaCheckSquare.vue';
import FaRegSquare from '@/components/icons/FaRegSquare.vue';
import RadioFilled from '@/components/icons/RadioFilled.vue';
import RadioEmpty from '@/components/icons/RadioEmpty.vue';
import { checkBetTransactionWarning, generateIssueMessage, isBetTransaction } from '@/common/transaction-utils';
import { ConnectToAgent, UserInputSubmittedTransactionForAiEvent } from '@/common/bot/types';
import { mergeSelectedTransactionsData, saveSelectedTransactionsToStorage } from '@/common/selectedTransactions.utils';
import { reportingService } from '@/common/reporting';
import * as PleasedApi from '@/common/api/api.pleased';
import * as PleasedModel from '@/common/api/models.pleased';
import { ChatWarnings } from '@/store/chatWarning/chatWarning.types';
import { ExternalTransactionKind } from '@/common/bot/enum/ExternalTransactionType';
import { betsTypeSwitch } from '@/common/sb-transaction.helpers';
import Api from '@/common/api';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import { mixins } from 'vue-class-component';
import { analytics } from '@/common/logs/EventLogger';
import { BTN_EVTS } from '@/directives/trackClick';
import { CRITICAL_EVENTS } from '@/common/logs';
import { isRequestLimitError, parseErrorMessage } from '@/common/error';
import { randomId } from '@/common/bot/lib';
import { ExternalBotEvent } from '@/common/bot/enum/ExternalBotEvent';
import BetTransactionItem from './BetTransactionItem.vue';
import ListControls from './ListControls.vue';
import { getTransactionStatusLabel } from '@/common/i18n-static/getTransactionStatusLabel';

const chatModule = namespace('chat');
const chatWarning = namespace('chatWarning');
const userModule = namespace('user');

const getTitile = (firstSelection: BetHistoryEntitySelection) => {
  const isOutright = firstSelection.eventId.includes('match');
  return isOutright ? `${firstSelection.home} vs ${firstSelection.away}` : firstSelection.tournamentName;
};

enum ButtonId {
  AI_FLOW_PROCEED = 'ai-flow-proceed',
  DIRECT_TRIGGER_TICKET_CREATION = 'direct-trigger-ticket-creation',
  PROCEED = 'proceed',
  CANCEL = 'cancel',
  END_CHAT = 'end-chat',
  GO_TO_TICKET = 'go-to-ticket',
  CLEAR = 'clear',
}

@Component({
  components: {
    Input,
    BottomWrapper,
    FaCheckSquare,
    FaRegSquare,
    RadioFilled,
    RadioEmpty,
    BetTransactionItem,
    ListControls,
  },
  filters: {
    getTitile(firstSelection: BetHistoryEntitySelection) {
      return getTitile(firstSelection);
    },
  },
})
export default class BetTransaction extends mixins(LogPageEventMixin) {
  @Prop({ default: null }) readonly transactionsSavedData: TransactionInputEvent | null = null;
  @Prop({ default: '' }) readonly transactionType!: TransactionType;
  @Prop() readonly connectConfig!: ConnectToAgent | null;
  @Prop({ default: null }) readonly externalTransactionKinds!: ExternalTransactionKind[];

  @Prop({ default: () => [] }) readonly ticketFields!: PleasedModel.TicketField[];
  @Prop({ default: () => ({ subject: '', description: '', email: '' }) }) readonly requestInfo!: {
    subject: string;
    description: string;
    email: string;
  };
  @Prop() readonly userSelections!: PleasedModel.UserSelection[];
  @Prop() readonly transactionFlow!: 'ai' | 'default';
  @Prop() readonly onShowRequestLimitModal!: () => void;

  @chatModule.Mutation('updateExtraTransactionInfo') updateExtraTransactionInfo!: Function;
  @chatWarning.Getter('chatWarnings') chatWarnings!: ChatWarnings;

  @userModule.State('pleasedUser') pleasedUser!: PleasedUserInfo;
  @userModule.State('initSelectedEntity') initSelectedEntity!: SelectedEntity | null;

  readonly TRANSACTIONS_PER_PAGE = 5;

  readonly VALIDATE_SELECTION_FOR_USER_EVENTS = true;

  // TODO: create betTransactions vuex module, similar to transactions
  // After that move logic there, check results.

  transactions: BetHistoryEntity[] = [];
  userEventsSelections: BetHistoryEntitySelection[] = [];
  expandedUserEventsSelections: string[] = [];
  loading = false;
  currentPage = 1;
  totalPages = 1;
  filterTransactionStatus: ExternalTransactionKind | null = null;

  selectedTransIssue: any[] = [];
  preSelectedBet: BetHistoryEntity | null = null;
  expandedTrans: string[] = [];
  noneSelected: boolean = false;
  actionType: string = 'Bet';
  isTicketCreationTriggered: boolean = false;
  lastCreatedDirectRequest: PleasedModel.TicketDetail | null = null;
  triggerTicketRequestMap: { [key: string]: PleasedModel.ActiveTicket[] } = {};

  errInfo = {
    isErr: false,
    message: '',
  };

  get initSelectedBet() {
    const entity = this.initSelectedEntity?.entity as BetHistoryEntity;
    const isEntityInTransactions = this.transactions.some((transaction) => transaction.shortId === entity?.shortId);
    return entity && !isEntityInTransactions ? entity : null;
  }

  get uiTransactions(): BetHistoryEntity[] {
    if (this.currentPage === 1 && this.initSelectedBet) {
      return [this.initSelectedBet, ...this.transactions];
    }

    return this.transactions;
  }

  get title() {
    if (this.lastCreatedDirectRequest) {
      return this.tConversation('global.ticket.ticket_created', { ticketId: this.lastCreatedDirectRequest.id });
    }

    if (this.isNoRecord) {
      return this.tConversation('page.issue.transaction.none');
    }

    return `${this.tConversation('page.issue.bet.select_recent')}`;
  }

  get isNoRecord() {
    return this.uiTransactions.length === 0;
  }

  get isSelectedEntityShown() {
    return this.uiTransactions.some((transaction) => transaction.shortId === this.preSelectedBet?.shortId);
  }

  get canProceed() {
    if (this.VALIDATE_SELECTION_FOR_USER_EVENTS && this.showUserEventsSelections && !this.userEventsSelections.length)
      return false;

    const hasSelectedTx = this.selectedTransIssue.length > 0 || this.isSelectedEntityShown;
    const isNoTx = this.isNoRecord && !this.filterTransactionStatus;

    return hasSelectedTx || this.noneSelected || isNoTx;
  }

  get selectedTransactionWithWarning() {
    return (this.selectedTransIssue || []).some(
      (transaction) =>
        checkBetTransactionWarning({
          transaction,
          chatWarnings: this.chatWarnings,
        }).show
    );
  }

  get triggerTicketCreation() {
    const { triggerTicketCreation } = this.connectConfig || {};

    return !!triggerTicketCreation && this.selectedTransIssue.length > 0;
  }

  get ticketCreationResponseMessage() {
    return this.connectConfig?.ticketCreationResponseMessage;
  }

  get triggerTicketCreationError() {
    if (!this.triggerTicketCreation) {
      return '';
    }

    const isSelectedTransactionAlreadyReported = this.selectedTransIssue.some((tx) => {
      const { shortId } = tx;
      return this.triggerTicketRequestMap[shortId]?.length > 0;
    });

    if (isSelectedTransactionAlreadyReported) {
      return this.tConversation('page.issue.transaction.duplicate_direct_ticket_error');
    }

    return '';
  }

  get isSingleTransactionSelectMode() {
    const { triggerTicketCreation } = this.connectConfig || {};

    const isAiFlow = this.transactionFlow === 'ai';

    // if triggerTicketCreation is not set, then we don't need to check
    // for unsettled bet limitation (1 transaction per request)
    return triggerTicketCreation || isAiFlow;
  }

  get buttons() {
    const clearButton =
      this.currentPage > 1 || this.filterTransactionStatus
        ? [{ name: this.tConversation('page.issue.transaction.clear_filters'), id: ButtonId.CLEAR as const }]
        : [];

    if (this.transactionFlow === 'ai') {
      return [
        ...clearButton,
        {
          name: this.tConversation('page.guest.button.proceed'),
          id: ButtonId.AI_FLOW_PROCEED as const,
          className: 'secondary',
          isActive: this.canProceed,
          disabled: !this.canProceed,
        },
      ];
    }

    if (this.isTicketCreationTriggered) {
      if (this.errInfo.isErr) {
        return [
          {
            name: this.tConversation('global.end_chat'),
            id: ButtonId.END_CHAT,
            isActive: true,
          },
        ];
      }

      return [
        {
          // TODO: make separate row in i18n
          name: this.tConversation('article.feedback.button.need_more_help'),
          id: ButtonId.GO_TO_TICKET,
          isActive: false,
          className: 'secondary',
        },
        {
          name: this.tConversation('global.end_chat'),
          id: ButtonId.END_CHAT,
          isActive: true,
        },
      ];
    }

    if (this.triggerTicketCreation) {
      if (this.triggerTicketCreationError) {
        return [
          {
            name: this.tConversation('global.end_chat'),
            id: ButtonId.END_CHAT,
            isActive: true,
          },
        ];
      }

      return [
        {
          name: this.tConversation('page.issue.transaction.submit_request'),
          id: ButtonId.DIRECT_TRIGGER_TICKET_CREATION,
          isActive: true,
        },
      ];
    }

    return [
      ...clearButton,
      {
        name: this.selectedTransactionWithWarning
          ? this.tConversation('page.issue.transaction.proceed_anyway')
          : this.tConversation('page.guest.button.proceed'),
        id: ButtonId.PROCEED,
        isActive: this.canProceed,
        disabled: !this.canProceed,
        className: this.selectedTransactionWithWarning ? 'secondary' : '',
      },
      ...(this.selectedTransactionWithWarning
        ? [
            {
              name: this.tConversation('page.issue.transaction.cancel_request'),
              id: ButtonId.CANCEL,
              isActive: true,
            },
          ]
        : []),
    ];
  }

  get [PAGE_TRACKING]() {
    return {
      name: PAGE_EVENTS.CONNECT_REQUEST_TRANSACTION,
      metadata: {
        transactionType: this.actionType,
        transactionSubTypes: this.externalTransactionKinds,
      },
    };
  }

  get firstSelectedTransaction() {
    return this.selectedTransIssue?.[0] || this.preSelectedBet;
  }

  get isMultipleSelectionsPresent() {
    return this.firstSelectedTransaction?.selections?.length > 1;
  }

  get isAiFlow() {
    return this.transactionFlow === 'ai';
  }

  get showUserEventsSelections() {
    return this.isAiFlow && this.isMultipleSelectionsPresent;
  }

  get availableTransactionStatuses() {
    return this.externalTransactionKinds.map((kind) => ({
      value: kind,
      label: getTransactionStatusLabel(kind, this.tConversation.bind(this)),
    }));
  }

  isSelectedTransaction(transaction: BetHistoryEntity) {
    if (this.preSelectedBet && transaction.shortId === this.preSelectedBet.shortId) {
      return true;
    }

    return this.selectedTransIssue.some((tx) => tx.shortId === transaction.shortId);
  }

  isUserEventSelectionSelected(selection: BetHistoryEntitySelection) {
    return this.userEventsSelections.some((item) => item.id === selection.id);
  }

  updatedTransaction(transaction: BetHistoryEntity, selection: BetHistoryEntitySelection) {
    return { ...transaction, selections: [selection] };
  }

  created() {
    document.title = 'Bet Transactions';
    fetch && fetch('@/assets/img/checked.svg'); // Preload the image

    this.loadTransactions();

    if (this.transactionsSavedData) {
      this.selectedTransIssue = this.transactionsSavedData.selectedTransIssue;
    }

    if (this.initSelectedEntity && isBetTransaction(this.initSelectedEntity.entity, this.transactionType)) {
      this.preSelectedBet = this.initSelectedEntity.entity;
    }
  }

  private buildTransactionStatusCodes(transactionTypes: ExternalTransactionKind[]): string {
    return transactionTypes
      .map((transactionType) => betsTypeSwitch.match(transactionType))
      .filter((status) => !!status)
      .join(',');
  }

  private buildTransactionHistoryParams(statusCodes: string): Record<string, any> {
    if (!statusCodes) {
      return {
        isSettled: 10,
      };
    }

    return {
      type: this.transactionType,
      winningStatus: statusCodes,
    };
  }

  private calculateTotalPages(totalTransactions: number): number {
    return Math.ceil((totalTransactions || 1) / this.TRANSACTIONS_PER_PAGE);
  }

  async loadTransactions(
    pageNo: number = this.currentPage,
    status: ExternalTransactionKind | null = this.filterTransactionStatus
  ) {
    this.loading = true;

    const transactionTypes = status ? [status] : this.externalTransactionKinds;
    const betSubTypeCodes = this.buildTransactionStatusCodes(transactionTypes);
    const params = this.buildTransactionHistoryParams(betSubTypeCodes);

    const transactions = await Api.getBetHistory({
      ...params,
      pageSize: this.TRANSACTIONS_PER_PAGE,
      pageNo,
      _t: +new Date(),
    });

    this.transactions = transactions.data.entityList;
    this.totalPages = this.calculateTotalPages(transactions.data.totalNum);
    this.loading = false;
  }

  selectIssueTransactions(statement: BetHistoryEntity) {
    this.userEventsSelections = [];
    this.preSelectedBet = null;

    const index = this.selectedTransIssue.findIndex((trans) => trans.shortId === statement.shortId);
    const exist = index !== -1;

    analytics.trackClick({
      name: BTN_EVTS.CONNECT_REQUEST_TRANSACTION_SELECTED,
      selection: statement.shortId,
      metadata: {
        transaction: statement,
        transactionType: this.actionType,
        selected: !exist,
      },
    });

    if (exist) {
      this.selectedTransIssue.splice(index, 1);
    } else {
      this.noneSelected = false;

      if (this.isSingleTransactionSelectMode) {
        this.selectedTransIssue = [statement];
      } else {
        this.selectedTransIssue.push({ ...statement });
      }
    }
  }

  handleSelectUserEventsSelections(statement: BetHistoryEntitySelection) {
    const index = this.userEventsSelections.findIndex((selection) => selection.id === statement.id);
    const exist = index !== -1;
    if (exist) {
      this.userEventsSelections.splice(index, 1);
    } else {
      this.userEventsSelections.push({ ...statement });
    }
  }

  handleExpandUserSelections(id: string) {
    const index = this.expandedUserEventsSelections.indexOf(id);
    const exist = index !== -1;
    if (exist) {
      this.expandedUserEventsSelections.splice(index, 1);
    } else {
      this.expandedUserEventsSelections.push(id);
    }
  }

  expandTrans(shortId: string) {
    const index = this.expandedTrans.indexOf(shortId);
    const exist = index !== -1;
    if (exist) {
      this.expandedTrans.splice(index, 1);
    } else {
      this.expandedTrans.push(shortId);
    }
  }
  selectNone() {
    this.preSelectedBet = null;

    if (this.noneSelected) {
      this.noneSelected = false;
    } else {
      this.selectedTransIssue = [];
      this.noneSelected = true;
    }
  }

  async directTriggerTicketCreation() {
    const { category, title } = this.connectConfig || {};
    const transactionId = this.firstSelectedTransaction.shortId;

    const openedTickets = await PleasedApi.getActiveConversations({ onlyActive: true, transactionId });

    if (openedTickets.tickets.length > 0) {
      Vue.set(this.triggerTicketRequestMap, transactionId, openedTickets.tickets);
      return;
    }

    reportingService.openRequestClicked();

    const transactionUserSelections = this.selectedTransIssue.map((tx) => ({
      createdAt: `${Date.now()}`,
      message: `${SYSTEM_MESSAGE_PREFIX} ${this.t('chat.user.issue.part')}: ${generateIssueMessage({
        type: this.transactionType,
        transaction: tx,
      })}`,
    }));

    // For now we'll support only 1 transaction per request
    const subjectIds = `(Id${this.selectedTransIssue.length > 1 ? 's' : ''}: ${this.selectedTransIssue
      .map((tx) => tx.shortId)
      .join(', ')})`;
    const subject = `${category} - ${title} - ${subjectIds}`;
    const hadAiConversation = this.connectConfig?.aiConversation;

    const tags = [HardcodedTags.DIRECT_TICKET_TAG].filter(notEmpty);

    const { resumeAiSessionId } = this.connectConfig || { resumeAiSessionId: null };
    const payload = {
      subject,
      // TODO: move to i18n
      description: 'Direct Ticket Creation Request',
      recipientAddress: this.requestInfo.email,
      ticketFields: this.ticketFields,
      userSelections: [...this.userSelections, ...transactionUserSelections],
      userLogs: reportingService.getLogs(),
      tags,
      transactionId,
      chatbotSessionId: analytics.sessionId,
      ...(hadAiConversation ? { aiSessionId: resumeAiSessionId || analytics.sessionId } : {}),
    };

    PleasedApi.createTicket(payload)
      .then((res) => {
        this.lastCreatedDirectRequest = res;
        this.isTicketCreationTriggered = true;
        this.$emit('open-request', res.id);
      })
      .catch((e) => {
        const error: Error = e;

        if (isRequestLimitError(error)) {
          this.onShowRequestLimitModal();
          return;
        }

        analytics.trackCriticalEvent({
          name: CRITICAL_EVENTS.CONNECT_OPTIONS_OPEN_REQUEST_INITIATED_ERROR,
          metadata: {
            error: parseErrorMessage(error),
            payload,
          },
        });

        this.errInfo.isErr = true;
        this.errInfo.message = error.message;
      });
  }

  proceed(selectedButton: { id: string }) {
    const isSubmitAction = selectedButton.id === ButtonId.AI_FLOW_PROCEED || selectedButton.id === ButtonId.PROCEED;
    // If we proceed with no txs selected, but we have a selected entity, then we need to select the entity
    if (isSubmitAction && this.isSelectedEntityShown && !this.selectedTransIssue.length) {
      this.selectedTransIssue = [this.preSelectedBet];
    }

    if (selectedButton.id === ButtonId.AI_FLOW_PROCEED) {
      const emitAiFlowEvent = (metadata: PleasedModel.ExtendedTransStatement): void => {
        // Create updated connectConfig with selected bets, merging with existing data
        const newSelectedTransactionsData = {
          betsSelected: [...this.selectedTransIssue],
          userEventsSelections: [...this.userEventsSelections],
          withdrawals: [],
          deposits: [],
        };

        const mergedSelectedTransactionsData = mergeSelectedTransactionsData(
          this.connectConfig?.selectedTransactionsData,
          newSelectedTransactionsData
        );

        const updatedConnectConfig: ConnectToAgent = {
          ...this.connectConfig,
          selectedTransactionsData: mergedSelectedTransactionsData,
        } as ConnectToAgent;

        // Save to localStorage for persistence across page refreshes
        saveSelectedTransactionsToStorage(mergedSelectedTransactionsData);

        const event: UserInputSubmittedTransactionForAiEvent = {
          id: randomId(),
          type: ExternalBotEvent.USER_SUBMIT_INPUT_TRANSACTION_FOR_AI,
          payload: {
            transactionType: this.transactionType,
            transactionMetadata: metadata,
            connectConfig: updatedConnectConfig,
          },
        };

        this.$emit('transaction:ai-flow-input', event);
      };

      const transaction = this.firstSelectedTransaction;
      if (this.noneSelected || !transaction) return emitAiFlowEvent({ transactionNotSelected: true });

      const transactionMetadata = {
        transactionNotSelected: false,
        ...transaction,
        SELECTED_ENTITY_TYPE: 'BET',
        tradeId: transaction.shortId,
        __USER_EVENTS: this.userEventsSelections,
        __VALIDATED_FOREIGN_ID: this.pleasedUser.initForeignId,
      };

      return emitAiFlowEvent(transactionMetadata);
    }

    if (selectedButton.id === ButtonId.CANCEL) {
      this.$emit('cancel:request', this.connectConfig);
      return;
    }

    if (selectedButton.id === ButtonId.END_CHAT) {
      this.$emit('end-chat');
      return;
    }

    if (selectedButton.id === ButtonId.DIRECT_TRIGGER_TICKET_CREATION) {
      this.directTriggerTicketCreation();
      return;
    }

    if (selectedButton.id === ButtonId.GO_TO_TICKET) {
      this.$emit('open-active-conversation', this.lastCreatedDirectRequest);
      return;
    }

    if (selectedButton.id === ButtonId.CLEAR) {
      this.currentPage = 1;
      this.filterTransactionStatus = null;
      this.selectedTransIssue = [];
      return;
    }

    if (!this.canProceed) return;

    const issueMsg = this.selectedTransIssue.map((tx) =>
      generateIssueMessage({
        type: this.transactionType,
        transaction: tx,
      })
    );

    const data = [
      {
        extraFieldKey: 'Transaction',
        extraFieldValue: this.selectedTransIssue.reduce((acc, cur) => {
          const msg = `${OrderType[cur.orderType]} ${this.actionType}: [${getTitile(cur.selections[0])}]`;
          return acc ? `${acc} / ${msg}` : `${msg}`;
        }, ''),
      },
      {
        extraFieldKey: 'Transaction ID',
        extraFieldValue: this.selectedTransIssue.reduce(
          (acc, cur) => (acc ? `${acc} / ${cur.shortId}` : `${cur.shortId}`),
          ''
        ),
      },
    ];
    this.updateExtraTransactionInfo(data);

    // Create updated connectConfig with selected bets, merging with existing data
    const newSelectedTransactionsData = {
      betsSelected: [...this.selectedTransIssue],
      userEventsSelections: [...this.userEventsSelections],
      withdrawals: [],
      deposits: [],
    };

    const updatedConnectConfig: ConnectToAgent = {
      ...this.connectConfig,
      selectedTransactionsData: newSelectedTransactionsData,
    } as ConnectToAgent;
    const $event: TransactionInputEvent = {
      type: TransactionType.Bet,
      issues: issueMsg,
      none: this.noneSelected,
      meta: data,
      empty: this.isNoRecord,
      selectedTransIssue: this.selectedTransIssue,
      connectConfig: updatedConnectConfig,
    };

    this.$emit('transaction:input', $event);
  }

  cleanErr() {
    this.errInfo.isErr = false;
    this.errInfo.message = '';
  }

  @Watch('currentPage')
  @Watch('filterTransactionStatus')
  onFilterChange() {
    this.selectedTransIssue = [];
    this.loadTransactions();
  }
}
</script>

<style lang="scss">
@import './index.scss';
</style>
